import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Loader2, AlertCircle, RefreshCw, Crown, Check, Search,
} from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import ShareCard from '../components/ShareCard';
import { useAuth, Header } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PassSuccessPage — confirmation page after successful Freestays Pass purchase.
 * Route: /pass/success?session_id=...
 *
 * Extracted from App.js (Fase 1) — pure relocation, no logic changes.
 */
export const PassSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(null);
  const maxRetries = 10; // Max 10 retries (20 seconds total)

  const { data: passData, isLoading } = useQuery({
    queryKey: ['pass-verify', sessionId, retryCount],
    queryFn: async () => {
      const response = await axios.get(`${API}/pass/verify/${sessionId}`, {
        headers: localStorage.getItem('freestays_token')
          ? { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
          : {}
      });
      return response.data;
    },
    enabled: !!sessionId && retryCount < maxRetries,
    retry: false,
    onError: (err) => {
      console.error('Verification error:', err);
      if (retryCount < maxRetries) {
        setTimeout(() => setRetryCount(r => r + 1), 2000);
      } else {
        setError('Unable to verify payment. Please check your dashboard or contact support.');
      }
    }
  });

  // Retry if payment not yet completed
  useEffect(() => {
    if (passData && !passData.success && retryCount < maxRetries) {
      const timer = setTimeout(() => setRetryCount(r => r + 1), 2000);
      return () => clearTimeout(timer);
    }
  }, [passData, retryCount]);

  useEffect(() => {
    if (passData?.success) {
      // Refresh user data to get updated pass info
      checkAuth();
    }
  }, [passData?.success, checkAuth]);

  // Show error after max retries
  if (error || (retryCount >= maxRetries && !passData?.success)) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <Header />
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-amber-600" />
          </div>
          <h1 className="font-bold text-3xl mb-4">Payment Processing</h1>
          <p className="text-muted-foreground mb-8">
            Your payment is being processed. This may take a few moments.
            If you've completed payment, your pass will be activated shortly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !passData?.success) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Header />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="font-sans font-bold text-2xl mb-2">Processing Your Purchase...</h2>
          <p className="text-muted-foreground">Please wait while we activate your pass</p>
          <p className="text-xs text-muted-foreground mt-4">Attempt {retryCount + 1} of {maxRetries}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="pass-success">
      <Header />
      <div className="max-w-lg mx-auto px-4 text-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6 animate-bounce-gentle">
          <Crown className="w-12 h-12 text-white" />
        </div>

        <h1 className="font-bold text-3xl mb-4">Welcome to Freestays!</h1>
        <p className="text-muted-foreground mb-8">
          {passData.message}
        </p>

        <Card className="p-6 mb-8 bg-gradient-to-br from-[#00A9E0]/5 to-accent/5 border-[#00A9E0]/20">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Your Pass Code</p>
              <p className="font-mono text-2xl font-bold text-[#00A9E0] tracking-wider">{passData.pass_code}</p>
            </div>
            <Separator />
            <div className="flex items-center justify-center gap-2 text-sm">
              <Badge className={passData.pass_type === 'annual' ? 'bg-accent' : 'bg-primary'}>
                {passData.pass_type === 'annual' ? 'Annual Pass' : 'One-Time Pass'}
              </Badge>
              {passData.pass_type === 'annual' && (
                <span className="text-muted-foreground">Valid for 1 year</span>
              )}
            </div>
          </div>
        </Card>

        <div className="bg-gray-50 dark:bg-[#0a2540] rounded-xl p-4 mb-8 text-left">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Check className="w-5 h-5 text-[#00A9E0]" />
            What You Get
          </h3>
          {passData.pass_type === 'annual' ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Unlimited free rooms for a whole year. For frequent travelers.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span className="font-semibold text-primary">Unlimited FREE rooms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>Only pay for meals (HB/FB/AI)</span>
                </li>
              </ul>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Perfect for a single trip. Get your room free on your next booking.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span className="font-semibold text-primary">Room price = FREE</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>Only pay for meals (HB/FB/AI)</span>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Share with Referral Code - Phase 8 */}
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-3 text-center">Invite friends and earn up to 50% cash back!</p>
          <div className="flex justify-center">
            <ShareCard
              type="referral"
              data={{
                code: passData.referral_code || passData.pass_code || ''
              }}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate('/')} className="rounded-full" data-testid="start-booking-btn">
            <Search className="w-4 h-4 mr-2" />
            Start Booking Hotels
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full">
            View Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PassSuccessPage;
